/**
 * Inline configurator component with cascading dropdowns
 * for truck make, model, year, bed size, and in-bed accessories.
 * Renders inline (not in a modal) as a horizontal form row.
 *
 * Vanilla custom element — no Horizon theme dependencies.
 */
class ConfiguratorInline extends HTMLElement {
  /** @type {object|null} */
  #data = null;

  /** @type {object|null} */
  #selectedInbedEntry = null;

  connectedCallback() {
    this.#cacheRefs();
    this._isCoversPage = window.location.pathname === '/collections/covers';
    this._coversUrl = this.getAttribute('data-covers-url') || '/collections/covers';
    this.#loadData();
    if (!this.#data) return;

    this.#populateMakes();
    this.#restoreFromLocalStorage();
    this.#bindEvents();

    // On mobile, start collapsed if no truck is saved
    if (this.#isMobile() && !localStorage.getItem('configuratorTruckInfo')) {
      this.#collapseFields();
    }

    document.addEventListener('configurator:update', this.#handleExternalUpdate);
    window.addEventListener('storage', this.#handleStorageChange);
  }

  disconnectedCallback() {
    document.removeEventListener('configurator:update', this.#handleExternalUpdate);
    window.removeEventListener('storage', this.#handleStorageChange);
  }

  #cacheRefs() {
    this._makeSelect = this.querySelector('[data-ref="makeSelect"]');
    this._modelSelect = this.querySelector('[data-ref="modelSelect"]');
    this._yearSelect = this.querySelector('[data-ref="yearSelect"]');
    this._bedSelect = this.querySelector('[data-ref="bedSelect"]');
    this._inbedSelect = this.querySelector('[data-ref="inbedSelect"]');
    this._inbedField = this.querySelector('[data-ref="inbedField"]');
    this._submitButton = this.querySelector('[data-ref="submitButton"]');
    this._confirmedDisplay = this.querySelector('[data-ref="confirmedDisplay"]');
    this._confirmedValue = this.querySelector('[data-ref="confirmedValue"]');
    this._resetLink = this.querySelector('[data-ref="resetLink"]');
    this._shopCoversButton = this.querySelector('[data-ref="shopCoversButton"]');
    this._fieldsContainer = this.querySelector('[data-ref="fieldsContainer"]');
    this._headingEl = this.querySelector('.configurator-inline__heading');
    this._bedMirror = this.querySelector('[data-ref="bedMirror"]');
    this._inbedMirror = this.querySelector('[data-ref="inbedMirror"]');
  }

  #bindEvents() {
    this._makeSelect?.addEventListener('change', () => this.#handleMakeChange());
    this._modelSelect?.addEventListener('change', () => this.#handleModelChange());
    this._yearSelect?.addEventListener('change', () => this.#handleYearChange());
    this._bedSelect?.addEventListener('change', () => this.#handleBedChange());
    this._inbedSelect?.addEventListener('change', () => this.#handleInbedChange());
    this._submitButton?.addEventListener('click', () => this.#handleSubmit());
    this._resetLink?.addEventListener('click', (e) => {
      e.preventDefault();
      this.#handleReset();
    });
    this._shopCoversButton?.addEventListener('click', () => {
      window.location.href = this._coversUrl;
    });
    this._makeSelect?.addEventListener('focus', () => this.#expandFields());
    this._makeSelect?.addEventListener('click', () => this.#expandFields());
  }

  #loadData() {
    if (this.#data) return;
    const el = document.getElementById('configurator-data');
    if (!el) {
      this.#reportError('configurator_data_load_failed', { source: 'inline', reason: 'element_missing' });
      return;
    }
    try {
      this.#data = JSON.parse(el.textContent);
    } catch (e) {
      this.#reportError('configurator_data_load_failed', { source: 'inline', reason: 'parse_error' });
    }
  }

  // -- Populate helpers --

  #populateMakes() {
    const select = this._makeSelect;
    this.#resetSelect(select, 'Make');
    for (const entry of this.#data.options) {
      select.add(new Option(entry.make, entry.make));
    }
  }

  #populateModels(make) {
    const makeEntry = this.#data.options.find((e) => e.make === make);
    if (!makeEntry) return;

    const select = this._modelSelect;
    this.#resetSelect(select, 'Model');
    for (const entry of makeEntry.models) {
      select.add(new Option(entry.model, entry.model));
    }
    select.removeAttribute('disabled');
  }

  #populateYears(make, model) {
    const makeEntry = this.#data.options.find((e) => e.make === make);
    const modelEntry = makeEntry?.models.find((e) => e.model === model);
    if (!modelEntry) return;

    const select = this._yearSelect;
    this.#resetSelect(select, 'Year Range');
    const sortedYears = [...modelEntry.years].sort((a, b) => parseInt(b.year) - parseInt(a.year));
    const seenYears = new Set();
    for (const entry of sortedYears) {
      if (seenYears.has(entry.year)) continue;
      seenYears.add(entry.year);
      select.add(new Option(entry.year, entry.year));
    }
    select.removeAttribute('disabled');
  }

  #populateBeds(make, model, year) {
    const makeEntry = this.#data.options.find((e) => e.make === make);
    const modelEntry = makeEntry?.models.find((e) => e.model === model);
    const yearEntry = modelEntry?.years.find((e) => e.year === year);
    if (!yearEntry) return;

    const select = this._bedSelect;
    this.#resetSelect(select, 'Bed Length');
    const seenBeds = new Set();
    for (const entry of yearEntry.beds) {
      if (seenBeds.has(entry.bed)) continue;
      seenBeds.add(entry.bed);
      select.add(new Option(entry.bed, entry.bed));
    }
    select.removeAttribute('disabled');
  }

  // -- Cascade handlers --

  #handleMakeChange() {
    const make = this._makeSelect.value;
    this.#hideFieldsFrom('modelSelect');
    this.#hideSubmit();

    if (!make) return;
    this.#populateModels(make);
  }

  #handleModelChange() {
    const make = this._makeSelect.value;
    const model = this._modelSelect.value;
    this.#hideFieldsFrom('yearSelect');
    this.#hideSubmit();

    if (!model) return;
    this.#populateYears(make, model);
  }

  #handleYearChange() {
    const make = this._makeSelect.value;
    const model = this._modelSelect.value;
    const year = this._yearSelect.value;
    this.#hideFieldsFrom('bedSelect');
    this.#hideSubmit();

    if (!year) return;
    this.#populateBeds(make, model, year);
  }

  #handleBedChange() {
    const make = this._makeSelect.value;
    const model = this._modelSelect.value;
    const year = this._yearSelect.value;
    const bed = this._bedSelect.value;
    this.#hideFieldsFrom('inbedSelect');
    this.#hideSubmit();

    if (this._bedMirror) this._bedMirror.textContent = bed || 'Bed Length';

    if (!bed) return;

    const bedEntry = this.#findBedEntry(make, model, year, bed);
    if (!bedEntry) {
      this.#reportError('configurator_entry_not_found', { source: 'inline', make, model, year, bed });
      return;
    }

    const hasInbedOptions = bedEntry.inbeds.some(
      (e) => e.inbed === 'Yes' || e.inbed === 'No'
    );

    if (hasInbedOptions) {
      const select = this._inbedSelect;
      this.#resetSelect(select, 'In-Bed Track');
      select.add(new Option('No', 'No'));
      select.add(new Option('Yes', 'Yes'));
      this._inbedField.removeAttribute('hidden');
      select.removeAttribute('disabled');
    } else {
      this.#selectedInbedEntry = bedEntry.inbeds[0];
      const urls = this.#selectedInbedEntry?.urls || {};
      this.#autoSave({ make, model, year, bed, inbed: '' }, urls);
      this.#pushGA4Event('configurator_completed', { source: 'inline', make, model, year, bed, inbed: '' });
      this.#showSubmit();
    }
  }

  #handleInbedChange() {
    const inbedValue = this._inbedSelect.value;
    this.#hideSubmit();

    if (this._inbedMirror) this._inbedMirror.textContent = inbedValue || 'In-Bed Track';

    if (!inbedValue) return;

    const make = this._makeSelect.value;
    const model = this._modelSelect.value;
    const year = this._yearSelect.value;
    const bed = this._bedSelect.value;

    const bedEntry = this.#findBedEntry(make, model, year, bed);
    if (!bedEntry) {
      this.#reportError('configurator_entry_not_found', { source: 'inline', make, model, year, bed });
      return;
    }

    this.#selectedInbedEntry = bedEntry.inbeds.find(
      (e) => e.inbed === inbedValue || (inbedValue === 'No' && e.inbed === '')
    );
    if (!this.#selectedInbedEntry) {
      this.#reportError('configurator_inbed_entry_not_found', { source: 'inline', make, model, year, bed, inbed: inbedValue });
      return;
    }
    const urls = this.#selectedInbedEntry?.urls || {};
    this.#autoSave({ make, model, year, bed, inbed: inbedValue }, urls);
    this.#pushGA4Event('configurator_completed', { source: 'inline', make, model, year, bed, inbed: inbedValue });
    this.#showSubmit();
  }

  #handleSubmit() {
    const selection = {
      make: this._makeSelect.value,
      model: this._modelSelect.value,
      year: this._yearSelect.value,
      bed: this._bedSelect.value,
      inbed: this._inbedSelect.value || '',
    };

    const urls = this.#selectedInbedEntry?.urls || {};

    localStorage.setItem('configuratorTruckInfo', JSON.stringify(selection));
    localStorage.setItem('configuratorUrlMap', JSON.stringify(urls));

    if (this._isCoversPage) {
      document.dispatchEvent(
        new CustomEvent('configurator:update', {
          detail: { selection, urls },
        })
      );
      this.#showConfirmedState(selection);
    } else {
      window.location.href = this._coversUrl;
    }
  }

  // -- Data lookup --

  #findBedEntry(make, model, year, bed) {
    const makeEntry = this.#data.options.find((e) => e.make === make);
    const modelEntry = makeEntry?.models.find((e) => e.model === model);
    const yearEntry = modelEntry?.years.find((e) => e.year === year);
    return yearEntry?.beds.find((e) => e.bed === bed) || null;
  }

  // -- UI helpers --

  #resetSelect(select, placeholder) {
    select.innerHTML = '';
    select.add(new Option(placeholder, ''));
  }

  #hideFieldsFrom(startRef) {
    const selects = ['modelSelect', 'yearSelect', 'bedSelect', 'inbedSelect'];
    const refs = {
      modelSelect: this._modelSelect,
      yearSelect: this._yearSelect,
      bedSelect: this._bedSelect,
      inbedSelect: this._inbedSelect,
    };
    const startIndex = selects.indexOf(startRef);
    if (startIndex === -1) return;

    const mirrorResets = {
      bedSelect: () => { if (this._bedMirror) this._bedMirror.textContent = 'Bed Length'; },
      inbedSelect: () => { if (this._inbedMirror) this._inbedMirror.textContent = 'In-Bed Track'; },
    };

    for (let i = startIndex; i < selects.length; i++) {
      const selectRef = selects[i];
      const select = refs[selectRef];
      select.setAttribute('disabled', '');
      const placeholder = select.options[0]?.text || '';
      this.#resetSelect(select, placeholder);
      mirrorResets[selectRef]?.();
    }

    this._inbedField.setAttribute('hidden', '');
    this.#selectedInbedEntry = null;
  }

  #showSubmit() {
    this._submitButton.removeAttribute('disabled');
    this._submitButton.textContent = this._isCoversPage ? 'Save Your Truck' : 'Shop Covers';
  }

  #hideSubmit() {
    this._submitButton.setAttribute('disabled', '');
    this._submitButton.textContent = this._isCoversPage ? 'Select Your Truck' : 'Shop Covers';
  }

  #isMobile() {
    return window.matchMedia('(max-width: 749px)').matches;
  }

  #collapseFields() {
    this._fieldsContainer?.classList.add('is-collapsed');
  }

  #expandFields() {
    this._fieldsContainer?.classList.remove('is-collapsed');
  }

  #showConfirmedState(selection) {
    const display = `${selection.year} ${selection.make} ${selection.model} | ${selection.bed} Bed`;
    this._confirmedValue.textContent = display;

    // Hide shop covers button when already on covers page
    if (this._shopCoversButton) {
      this._shopCoversButton.hidden = this._isCoversPage;
    }

    // Hide the form fields and heading
    this._fieldsContainer?.setAttribute('hidden', '');
    this._headingEl?.setAttribute('hidden', '');

    // Show confirmed display
    this._confirmedDisplay?.removeAttribute('hidden');
  }

  #handleReset() {
    // Clear localStorage
    localStorage.removeItem('configuratorTruckInfo');
    localStorage.removeItem('configuratorUrlMap');

    // Dispatch update event so other components (link rewriter, truck-info) react
    document.dispatchEvent(new CustomEvent('configurator:update', { detail: null }));

    // Hide confirmed display, show form
    this._confirmedDisplay?.setAttribute('hidden', '');
    this._fieldsContainer?.removeAttribute('hidden');
    this._headingEl?.removeAttribute('hidden');

    // Reset form state
    this.#populateMakes();
    this.#hideFieldsFrom('modelSelect');
    this.#hideSubmit();

    if (this.#isMobile()) {
      this.#collapseFields();
    }
  }

  // -- Restore state --

  #restoreFromLocalStorage() {
    let saved;
    try {
      const raw = localStorage.getItem('configuratorTruckInfo');
      if (!raw) return;
      saved = JSON.parse(raw);
      if (!saved?.make) return;
    } catch {
      return;
    }

    this._makeSelect.value = saved.make;
    if (!this._makeSelect.value) {
      this.#reportError('configurator_saved_mismatch_on_pageload', { source: 'inline', mismatched_field: 'make', saved }, 'warning');
      return;
    }
    this.#populateModels(saved.make);

    this._modelSelect.value = saved.model;
    if (!this._modelSelect.value) {
      const normModel = saved.model.replace(/\u2013/g, '-');
      for (const opt of this._modelSelect.options) {
        if (opt.value && opt.value.replace(/\u2013/g, '-') === normModel) {
          this._modelSelect.value = opt.value;
          saved.model = opt.value;
          break;
        }
      }
    }
    if (!this._modelSelect.value) {
      this.#reportError('configurator_saved_mismatch_on_pageload', { source: 'inline', mismatched_field: 'model', saved }, 'warning');
      return;
    }
    this.#populateYears(saved.make, saved.model);

    this._yearSelect.value = saved.year;
    if (!this._yearSelect.value) {
      const normYear = saved.year.replace(/\u2013/g, '-');
      for (const opt of this._yearSelect.options) {
        if (opt.value && opt.value.replace(/\u2013/g, '-') === normYear) {
          this._yearSelect.value = opt.value;
          saved.year = opt.value;
          break;
        }
      }
    }
    if (!this._yearSelect.value) {
      this.#reportError('configurator_saved_mismatch_on_pageload', { source: 'inline', mismatched_field: 'year', saved }, 'warning');
      return;
    }
    this.#populateBeds(saved.make, saved.model, saved.year);

    this._bedSelect.value = saved.bed;
    if (!this._bedSelect.value) {
      this.#reportError('configurator_saved_mismatch_on_pageload', { source: 'inline', mismatched_field: 'bed', saved }, 'warning');
      return;
    }
    if (this._bedMirror) this._bedMirror.textContent = saved.bed;

    const bedEntry = this.#findBedEntry(saved.make, saved.model, saved.year, saved.bed);
    if (!bedEntry) {
      this.#reportError('configurator_saved_mismatch_on_pageload', { source: 'inline', mismatched_field: 'bed_entry', saved }, 'warning');
      return;
    }

    const hasInbedOptions = bedEntry.inbeds.some(
      (e) => e.inbed === 'Yes' || e.inbed === 'No'
    );

    if (hasInbedOptions && saved.inbed) {
      const select = this._inbedSelect;
      this.#resetSelect(select, 'In-Bed Track');
      select.add(new Option('No', 'No'));
      select.add(new Option('Yes', 'Yes'));
      this._inbedField.removeAttribute('hidden');
      select.removeAttribute('disabled');
      select.value = saved.inbed;
      if (this._inbedMirror) this._inbedMirror.textContent = saved.inbed;
      this.#selectedInbedEntry = bedEntry.inbeds.find(
        (e) => e.inbed === saved.inbed || (saved.inbed === 'No' && e.inbed === '')
      );
    } else {
      this.#selectedInbedEntry = bedEntry.inbeds[0];
    }

    this.#showSubmit();

    // Show confirmed state on all pages when truck is already saved
    this.#showConfirmedState(saved);
   }

  // -- External sync --

  #handleExternalUpdate = () => {
    this.#restoreFromLocalStorage();
  };

  #reportError(eventName, params, level = 'error') {
    this.#pushGA4Event(eventName, params);
    if (window.Sentry) {
      Sentry.captureMessage(eventName, { level, extra: params });
    }
  }

  #autoSave(selection, urls) {
    localStorage.setItem('configuratorTruckInfo', JSON.stringify(selection));
    localStorage.setItem('configuratorUrlMap', JSON.stringify(urls));
  }

  #pushGA4Event(eventName, params) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(Object.assign({ event: eventName }, params));
  }

  #handleStorageChange = (event) => {
    if (event.key === 'configuratorTruckInfo') {
      if (!event.newValue && this._isCoversPage) {
        // Truck was cleared from another tab — reset UI
        this._confirmedDisplay?.setAttribute('hidden', '');
        this._fieldsContainer?.removeAttribute('hidden');
        this._headingEl?.removeAttribute('hidden');
      }
      this.#populateMakes();
      this.#hideFieldsFrom('modelSelect');
      this.#hideSubmit();
      this.#restoreFromLocalStorage();
    }
  };
}

if (!customElements.get('configurator-inline-component')) {
  customElements.define('configurator-inline-component', ConfiguratorInline);
}
